import React, {useMemo} from "react";
import HighchartsReact from "highcharts-react-official";
import { blue, lightBlue, grey, red } from '@mui/material/colors'

import {
    getDayOfYearLabel,
    getSeason,
    isLeapYear,
    partitionData,
    groupBy,
    generateStatisticsByKey,
    createDayOfMonthLabels,
    trimLabels,
    extractSeason, getSeasonLabel, extractAvailableSeasons
} from "../utils";
import Toolbar, {useToolbar} from "./Toolbar";
import Stack from "@mui/material/Stack";
import {useTranslation} from "react-i18next";
import Highcharts, {chartLogoOptions, creditsOptions, exportingOptions} from "../highChartsBase";
import useHighChartLocaleOptions from "../useHighChartLocaleOptions";


const HSGraph = ({data: originalData, station}) => {
    const {t} = useTranslation("graphs");
    const highChartLocaleOptions = useHighChartLocaleOptions();
    const data = useMemo(() => (originalData.map(({timestamp, ...rest}) => {
            return {
                timestamp,
                label: getDayOfYearLabel(timestamp),
                season: getSeason(timestamp),
                ...rest
            }
        })
    ), [originalData]);

    const availableSeasons = useMemo( () => extractAvailableSeasons(data), [data] );
    const toolbarState = useToolbar(availableSeasons);

    const selectedSeason = toolbarState.selectedSeason;
    const historyYearFrom = toolbarState.historyFrom;
    const historyYearTo = toolbarState.historyTo;
    const compareWith = toolbarState.compareWith;

    const [selectedData, historicData] = partitionData(selectedSeason, historyYearFrom, historyYearTo, data);
    const compareWithData = compareWith ? groupBy(extractSeason(data, compareWith), 'label') : [];

    const currentData = groupBy(selectedData, 'label');
    const historicStats = generateStatisticsByKey(historicData, ['hs'], 'label');

    const years = Array.from({length: historyYearTo - historyYearFrom + 1}, (_, i) => historyYearFrom + i);
    const withLeapYears = years.some(y => isLeapYear(y) + 1) || isLeapYear(selectedSeason + 1);
    const labels = createDayOfMonthLabels(withLeapYears);

    const trimmedLabels = trimLabels(labels, {...currentData, ...historicStats});

    const options = {
        ...highChartLocaleOptions,
        exporting: {
            ...exportingOptions.exporting,
            filename: `${station?.code ?? 'station'}_hs`
        },
        title: {
            text: `${station?.code ?? ''} - ${station?.name ?? ''} (${station?.elevation ?? '----'} m)`
        },
        subtitle: {
            text: t('hsGraph.title')
        },
        series: [
            {
                data: trimmedLabels.map(label => historicStats[label]?.hs?.min ?? null),
                type: 'area',
                name: t('hsGraph.legend.hsMin'),
                color: grey[700],
                fillOpacity: 0.1,
                yAxis: 0,
                marker: {
                    enabled: true,
                    radius: 2
                }
            },
            {
                data: trimmedLabels.map(label => historicStats[label]?.hs?.mean ?? null),
                type: 'area',
                name: t('hsGraph.legend.hsMean'),
                color: grey[500],
                fillOpacity: 0.1,
                yAxis: 0,
                marker: {
                    enabled: true,
                    radius: 2
                }
            },
            {
                data: trimmedLabels.map(label => historicStats[label]?.hs?.max ?? null),
                type: 'area',
                name: t('hsGraph.legend.hsMax'),
                color: grey[300],
                fillOpacity: 0.1,
                yAxis: 0,
                marker: {
                    enabled: true,
                    radius: 2
                }
            },
            {
                data: trimmedLabels.map(label => currentData[label]?.[0].hn ?? null),
                type: 'column',
                name: t('hsGraph.legend.hnSeason', {season: getSeasonLabel(selectedSeason)}),
                color: lightBlue[500],
                yAxis: 0,
            },
            {
                id: 'hs-season',
                data: trimmedLabels.map(label => currentData[label]?.[0].hs ?? null),
                type: 'line',
                name: t('hsGraph.legend.hsSeason', {season: getSeasonLabel(selectedSeason)}),
                color: blue[900],
                yAxis: 0,
                marker: {
                    enabled: true,
                    radius: 3
                }
            },
            {
                id: 'hs-season-compare',
                data: trimmedLabels.map(label => compareWithData[label]?.[0].hs ?? null),
                type: 'line',
                name: t('hsGraph.legend.hsSeason', {season: getSeasonLabel(compareWith)}),
                color: red[500],
                yAxis: 0,
                marker: {
                    enabled: true,
                    radius: 3
                }
            }
        ],
        xAxis: {
            categories: trimmedLabels,
            gridLineWidth: 1
        },
        yAxis: [
            { // First y-axis
                title: {
                    text: 'HS/HN [cm]',
                    gridLineWidth: 1
                }
            },
        ],
        tooltip: {
            shared: true,
            crosshairs: true,
            formatter: function () {
                let tooltip = '<b>' + this.x + '</b>';
                this.points.forEach(point => {
                    tooltip += '<br/><span style="color:' + point.series.color + '">●</span> ' + point.series.name + ': <b>' + Math.round(point.y) + '</b>';
                });
                return tooltip;
            }
        },
        plotOptions: {
            series: {
                marker: {
                    enabled: true,
                    symbol: 'circle',
                },
                line: {
                    connectNulls: false // Do not connect null points
                },
                area: {
                    connectNulls: false // Do not connect null points
                },
                states: {
                    inactive: {
                        opacity: 1 // Prevent fading by keeping opacity at 1
                    }
                }
            }
        },
        chart: {
            zoomType: 'x', // Enables horizontal zooming; use 'y' or 'xy' for vertical or both directions
            height: 500,
            ...chartLogoOptions
        },
        ...creditsOptions,
    }

    return <Stack>
        <Toolbar {...toolbarState} availableSeasons={availableSeasons} />
        <HighchartsReact
            highcharts={Highcharts}
            options={options}
        />
    </Stack>
}

export default HSGraph;